import { Marked, type RendererObject } from 'marked';
import DOMPurify from 'dompurify';
import { highlight } from './Highlighter.ts';
import { MarkdownContent } from './MarkdownContent.ts';
import { RenderedHtml } from './RenderedHtml.ts';

const LANGUAGE_NAME = /^[a-z0-9-]+$/;

const languageOf = (lang: string | undefined): string => {
  const name = (lang ?? '').split(/\s/, 1)[0].toLowerCase();
  return LANGUAGE_NAME.test(name) ? name : 'none';
};

const renderer: RendererObject = {
  heading({ tokens, depth }) {
    const adjustedDepth = Math.min(depth + 3, 5);
    const text = this.parser.parseInline(tokens);
    return `<h${depth} class="ippon-title -l${adjustedDepth}">${text}</h${depth}>`;
  },
  code({ text, lang }) {
    const language = languageOf(lang);
    return `<pre class="language-${language}"><code class="language-${language}">${highlight(text, language)}</code></pre>`;
  },
  paragraph({ tokens }) {
    const text = this.parser.parseInline(tokens);
    return `<p class="ippon-text -body">${text}</p>`;
  },
  table({ header, rows }) {
    const headerCells = header.map((cell) => this.tablecell(cell)).join('');
    const headerRow = this.tablerow({ text: headerCells });
    const bodyRows = rows
      .map((row) => {
        const cells = row.map((cell) => this.tablecell(cell)).join('');
        return this.tablerow({ text: cells });
      })
      .join('');
    return `<table class="ippon-table"><thead class="ippon-table--head">${headerRow}</thead><tbody class="ippon-table--body">${bodyRows}</tbody></table>`;
  },
  tablerow({ text }) {
    return `<tr class="ippon-table--row">${text}</tr>`;
  },
  tablecell(token) {
    const text = this.parser.parseInline(token.tokens);
    const tag = token.header ? 'th' : 'td';
    const className = token.header ? 'ippon-table--header' : 'ippon-table--cell';
    return `<${tag} class="${className}">${text}</${tag}>`;
  },
};

export class MarkdownRenderer {
  private readonly marked = new Marked({ renderer });

  render(content: MarkdownContent): RenderedHtml {
    const rawHtml = this.marked.parse(content.raw, {
      async: false,
    });
    const sanitizedHtml = DOMPurify.sanitize(rawHtml);
    return RenderedHtml.of(sanitizedHtml);
  }
}
